import React, { useCallback, useRef } from 'react';
import { BasicDropdownMenu, Dropdown, Icon } from '@placer-ui/components';
import styles from './assistant-chat-input.module.scss';
import cx from 'classnames';
import { IRichTextEditor, RichTextEditor } from './RichTextEditor';
import { noop } from 'lodash';
import { IImage, ScreenshotStatus, useScreenshotService } from '../../../../../infra';

interface AssistantChatInputProps {
    onSubmit?(content: string, imageDataUri: IImage | undefined): void;
    onTextChanged?(content: string): void;
    placeholder?: string;
    isReadOnly?: boolean;
    canSubmit?: boolean;
    content?: string;
    autofocus?: boolean;
    editorContentClassName?: string;
    textEditorContent?: string;
    onEditorReady?: (editor: IRichTextEditor) => void;

}

export const SCREENSHOT_IMAGE_QUALITY = 0.8;

export const AssistantChatInput: React.FC<AssistantChatInputProps> = ({
    onSubmit,
    onTextChanged,
    placeholder = 'Ask Pi Anything...',
    isReadOnly = false,
    canSubmit = true,
    content,
    autofocus = true,
    editorContentClassName,
    textEditorContent,
    onEditorReady,
}) => {
    const editorRef = useRef<IRichTextEditor | null>(null);
    const screenshotService = useScreenshotService();
    const [thumbnailUrl, setThumbnailUrl] = React.useState<string | undefined>(undefined);
    const [screenshotImage, setScreenshotImage] = React.useState<IImage | undefined>(undefined);
    const handleEditorCreated = useCallback((editor: IRichTextEditor) => {
        onEditorReady?.(editor);
        editorRef.current = editor;
    }, [onEditorReady]);

    const editor = editorRef.current;

    const handleSubmit = useCallback(
        (content: string, editor: IRichTextEditor) => {
            if (canSubmit) {
                onSubmit?.(content, screenshotImage);
                editor.clearContent();
            }
        },
        [onSubmit, canSubmit, screenshotImage],
    );

    const handleTextChanged = useCallback(
        (editor: IRichTextEditor) => {
            const text = editor.getText();
            onTextChanged?.(text);
        },
        [onTextChanged],
    );

    const handleTakeScreenshot = useCallback(async () => {
        const screenshotResult = await screenshotService.captureScreen();

        const { status, image } = screenshotResult;

        if (status === ScreenshotStatus.SUCCESS && image) {
            const fitImage = await image.fit(100, 100);
            const dataUri = await fitImage.getJPegDataUri(SCREENSHOT_IMAGE_QUALITY);
            setThumbnailUrl(dataUri);
            setScreenshotImage(image);
        }
    }, [screenshotService]);

    return (
        <div className={cx(styles.container, { [styles.editable]: !isReadOnly })}>
            {screenshotImage && (
                <div className={styles.screenshotContainer}>
                    <img src={thumbnailUrl} alt="Screenshot" className={styles.screenshot} />
                    <div className={styles.remove}>
                        <Icon.IconContainer
                            size="small"
                            shape="circle"
                            disabled={!canSubmit}
                            onClick={() => {
                                setScreenshotImage(undefined);
                            }}
                            testId="assistant-chat-input-add-button"
                        >
                            <Icon type="X" weight="outline" size={18} color="brand" />
                        </Icon.IconContainer>
                    </div>
                </div>
            )}
            <div className={styles.inputContainer}>
                {!isReadOnly && (
                    <div style={{ marginRight: '8px' }}>
                        <Dropdown
                            trigger={
                                <Icon.IconContainer
                                    size="medium"
                                    shape="square"
                                    borderRadius="2xl"
                                    disabled={!canSubmit}
                                    onClick={noop}
                                    testId="assistant-chat-input-add-button"
                                >
                                    <Icon type="Plus" weight="outline" size={24} color="brand" />
                                </Icon.IconContainer>
                            }
                        >
                            <div style={{ minWidth: '220px' }}>
                                <BasicDropdownMenu
                                    items={[
                                        {
                                            label: 'Capture Screen',
                                            value: 'capture-screen',
                                            onClick: handleTakeScreenshot,
                                            LeftComponent: (
                                                <Icon type="Image" size={16} color="brand" />
                                            ),
                                        },
                                    ]}
                                    onSelection={() => console.log('onSelection')}
                                    onItemSelection={() => console.log('onItemSelection')}
                                />
                            </div>
                        </Dropdown>
                    </div>
                )}
                <RichTextEditor
                    textEditorContent={textEditorContent}
                    onTextChanged={handleTextChanged}
                    onEditorReady={handleEditorCreated}
                    placeholder={placeholder}
                    content={content}
                    isReadOnly={isReadOnly}
                    autofocus={autofocus}
                    editorContentClassName={editorContentClassName}
                    handleKeyDown={(editor: IRichTextEditor, event: KeyboardEvent) => {
                        if (event.key === 'Enter') {
                            const markdownContent = editor.getMarkdownContent() ?? '';

                            if (markdownContent.trim() !== '') {
                                handleSubmit(markdownContent, editor);
                            }
                            event.preventDefault();
                            return true;
                        }

                        return false;
                    }}
                />
                {!isReadOnly && (
                    <div className={styles.iconContainer}>
                        <Icon.IconContainer
                            size="medium"
                            shape="circle"
                            disabled={!canSubmit}
                            onClick={() => {
                                if (!editor) return;
                                const markdownContent = editor.getMarkdownContent() || '';
                                if (markdownContent.trim() !== '') {
                                    handleSubmit(markdownContent, editor!);
                                }
                            }}
                            testId="assistant-chat-input-send-button"
                        >
                            <Icon type="PaperPlaneRight" size={24} color="brand" />
                        </Icon.IconContainer>
                    </div>
                )}
            </div>
        </div>
    );
};
